import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  PencilIcon,
  NoSymbolIcon,
  CheckCircleIcon,
  ClockIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { superAdminAPI } from '../services/api';
import toast from 'react-hot-toast';

// Reads subscription status/trial-end off a customer object. Supports both a
// flat `subscription_status` / `subscription_trial_ends_at` shape (in case the
// customers LIST endpoint is later extended to match) and the nested
// `subscription: { status, trial_ends_at }` shape already returned by the
// customer DETAIL endpoint. Returns null when neither is present so callers
// can skip rendering entirely — the list endpoint doesn't carry this today.
function getSubscriptionInfo(customer) {
  const status = customer.subscription_status || customer.subscription?.status;
  if (!status) return null;
  const trialEndsAt =
    customer.subscription_trial_ends_at || customer.subscription?.trial_ends_at || null;
  return { status, trialEndsAt };
}

function SubscriptionPill({ status, trialEndsAt }) {
  const styles = {
    trial: 'bg-blue-100 text-blue-800',
    trialing: 'bg-blue-100 text-blue-800',
    trial_expired: 'bg-orange-100 text-orange-800',
    canceled: 'bg-gray-100 text-gray-800',
    active: 'bg-green-100 text-green-800',
  };
  const cls = styles[status] || 'bg-gray-100 text-gray-800';
  let label = status.replace('_', ' ');
  if ((status === 'trial' || status === 'trialing') && trialEndsAt) {
    const d = new Date(trialEndsAt);
    label = `Trial ends ${d.toISOString().slice(0, 10)}`;
  }
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
      {label}
    </span>
  );
}

// Mock customer data (fallback for development)
const mockCustomers = [
  {
    id: '1',
    tenantId: '5418e478-60a1-70d7-7390-9c163fc0828b',
    companyName: 'Test Company Updated',
    contactEmail: 'brileydeveloper@gmail.com',
    contactName: 'John Doe',
    industry: 'Technology',
    subscriptionPlan: 'starter',
    isActive: true,
    onboardingCompleted: true,
    createdAt: '2025-01-15T10:30:00Z',
    documentsCount: 15,
    queriesThisMonth: 2847,
    lastActivity: '2025-01-20T14:22:00Z',
  },
  {
    id: '2',
    tenantId: 'customer-tenant-456',
    companyName: 'HealthTech Solutions',
    contactEmail: 'admin@healthtech.com',
    contactName: 'Sarah Chen',
    industry: 'Healthcare',
    subscriptionPlan: 'pro',
    isActive: true,
    onboardingCompleted: true,
    createdAt: '2025-01-10T08:15:00Z',
    documentsCount: 42,
    queriesThisMonth: 5623,
    lastActivity: '2025-01-20T16:45:00Z',
  },
  {
    id: '3',
    tenantId: 'customer-tenant-789',
    companyName: 'RetailMax Inc',
    contactEmail: 'support@retailmax.com',
    contactName: 'Marcus Rodriguez',
    industry: 'Retail',
    subscriptionPlan: 'enterprise',
    isActive: false,
    onboardingCompleted: true,
    createdAt: '2024-12-20T12:00:00Z',
    documentsCount: 128,
    queriesThisMonth: 0,
    lastActivity: '2025-01-18T09:30:00Z',
  },
  {
    id: '4',
    tenantId: 'customer-tenant-012',
    companyName: 'EduPlatform',
    contactEmail: 'admin@eduplatform.edu',
    contactName: 'Dr. Lisa Wang',
    industry: 'Education',
    subscriptionPlan: 'starter',
    isActive: true,
    onboardingCompleted: false,
    createdAt: '2025-01-19T16:20:00Z',
    documentsCount: 3,
    queriesThisMonth: 45,
    lastActivity: '2025-01-20T11:15:00Z',
  },
];

export default function Customers() {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [page, setPage] = useState(1);
  const [trialTarget, setTrialTarget] = useState(null); // customer obj for existing, 'new' for new user
  const [trialForm, setTrialForm] = useState({ days: 30, tenant_id: '', email: '', company_name: '', plan: 'starter' });
  const queryClient = useQueryClient();

  // Fetch non-onboarded Cognito users
  const { data: cognitoOnlyUsers = [] } = useQuery(
    'cognito-users-missing-profile',
    () => superAdminAPI.getCognitoUsers({ only_missing_profile: true }),
    { staleTime: 30000 }
  );

  // Fetch customers from API
  const { data: apiResponse, isLoading, error } = useQuery(
    ['customers', page, searchTerm, filterStatus],
    () => {
      const params = {
        page,
        limit: 20
      };
      
      // Only add search if it has a value
      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
      }
      
      // Only add status if it's not 'all'
      if (filterStatus && filterStatus !== 'all') {
        params.status = filterStatus;
      }
      
      return superAdminAPI.getAllCustomers(params);
    },
    { 
      staleTime: 30000,
      keepPreviousData: true,
      onError: (error) => {
        console.error('Failed to fetch customers:', error);
        // Note: onError doesn't return data, fallback logic is handled below
      }
    }
  );

  // Use API data if available, otherwise use mock data
  const customers = apiResponse?.customers || mockCustomers;
  const totalCustomers = apiResponse?.total || mockCustomers.length;

  // Suspend customer mutation
  const suspendMutation = useMutation(
    (tenantId) => superAdminAPI.suspendCustomer(tenantId),
    {
      onSuccess: () => {
        toast.success('Customer suspended successfully');
        queryClient.invalidateQueries('customers');
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Failed to suspend customer');
      }
    }
  );

  // Activate customer mutation
  const activateMutation = useMutation(
    (tenantId) => superAdminAPI.activateCustomer(tenantId),
    {
      onSuccess: () => {
        toast.success('Customer activated successfully');
        queryClient.invalidateQueries('customers');
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Failed to activate customer');
      }
    }
  );

  // Start/extend trial mutation
  const trialMutation = useMutation(
    ({ tenantId, payload }) => superAdminAPI.setCustomerTrial(tenantId, payload),
    {
      onSuccess: (data) => {
        const msg = data.profile_created
          ? `Trial started for ${data.company_name} (${data.trial_ends_at?.slice(0, 10)})`
          : `Trial set for ${data.company_name} — ends ${data.trial_ends_at?.slice(0, 10)}`;
        toast.success(msg);
        queryClient.invalidateQueries('customers');
        queryClient.invalidateQueries('cognito-users-missing-profile');
        closeTrialModal();
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Failed to set trial');
      }
    }
  );

  const openTrialForCustomer = (customer) => {
    setTrialTarget(customer);
    setTrialForm({
      days: 30,
      tenant_id: customer.tenant_id || customer.tenantId || '',
      email: customer.contact_email || customer.contactEmail || '',
      company_name: customer.company_name || customer.companyName || '',
      plan: customer.subscription_plan || customer.subscriptionPlan || 'starter',
    });
  };

  const openTrialForNewUser = () => {
    setTrialTarget('new');
    setTrialForm({ days: 30, tenant_id: '', email: '', company_name: '', plan: 'starter' });
  };

  const openTrialForCognitoUser = (cognitoUser) => {
    setTrialTarget('new');
    setTrialForm({
      days: 30,
      tenant_id: cognitoUser.sub || '',
      email: cognitoUser.email || '',
      company_name: cognitoUser.username || '',
      plan: 'starter',
    });
  };

  const closeTrialModal = () => {
    setTrialTarget(null);
  };

  const handleSubmitTrial = () => {
    if (!trialForm.tenant_id) {
      toast.error('Tenant ID is required');
      return;
    }
    if (!trialForm.days || trialForm.days < 1) {
      toast.error('Days must be at least 1');
      return;
    }
    const isNew = trialTarget === 'new';
    if (isNew && (!trialForm.email || !trialForm.company_name)) {
      toast.error('Email and Company name are required for a new user');
      return;
    }
    trialMutation.mutate({
      tenantId: trialForm.tenant_id,
      payload: {
        days: Number(trialForm.days),
        email: trialForm.email || undefined,
        company_name: trialForm.company_name || undefined,
        plan: trialForm.plan || undefined,
      },
    });
  };

  // Client-side filtering is handled by API now, so we just use the results directly
  const filteredCustomers = customers || [];

  const handleViewCustomer = (customer) => {
    setSelectedCustomer(customer);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (customer) => {
    if (!customer.onboarding_completed) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Pending</span>;
    }
    if (!customer.is_active) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Suspended</span>;
    }
    return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>;
  };

  const handleSuspend = async (customer) => {
    if (window.confirm(`Are you sure you want to suspend ${customer.company_name}?`)) {
      suspendMutation.mutate(customer.tenant_id);
    }
  };

  const handleActivate = async (customer) => {
    if (window.confirm(`Are you sure you want to activate ${customer.company_name}?`)) {
      activateMutation.mutate(customer.tenant_id);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Customer Management</h1>
        <p className="mt-2 text-gray-600">
          Manage all customer accounts and subscriptions
        </p>
      </div>

      {/* Actions */}
      <div className="mb-4 flex justify-end">
        <button
          onClick={openTrialForNewUser}
          className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700"
        >
          <PlusIcon className="h-4 w-4" />
          Start Trial for Unlisted User
        </button>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search customers..."
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="flex items-center">
          <FunnelIcon className="h-5 w-5 text-gray-400 mr-2" />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="all">All Customers</option>
            <option value="active">Active</option>
            <option value="inactive">Suspended</option>
            <option value="pending">Pending Onboarding</option>
          </select>
        </div>
      </div>

      {/* Non-onboarded Cognito Users */}
      {cognitoOnlyUsers.length > 0 && (
        <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-yellow-900">
              Not Onboarded ({cognitoOnlyUsers.length}) — signed up but never completed checkout
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-medium text-yellow-900 uppercase">
                  <th className="py-2 pr-4">Cognito Username</th>
                  <th className="py-2 pr-4">Email</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Created</th>
                  <th className="py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-yellow-200">
                {cognitoOnlyUsers.map((u) => (
                  <tr key={u.sub || u.username}>
                    <td className="py-2 pr-4 font-medium text-gray-900">{u.username}</td>
                    <td className="py-2 pr-4 text-gray-700">{u.email || '—'}</td>
                    <td className="py-2 pr-4">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        {u.status}{!u.enabled && ' (disabled)'}
                      </span>
                    </td>
                    <td className="py-2 pr-4 text-gray-500">{u.created_at ? formatDate(u.created_at) : '—'}</td>
                    <td className="py-2 text-right">
                      <button
                        onClick={() => openTrialForCognitoUser(u)}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-600 text-white text-xs font-medium rounded hover:bg-indigo-700"
                      >
                        Start Trial
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Customer Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Company
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Contact
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Plan
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Usage
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Joined
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredCustomers.map((customer) => {
              const subInfo = getSubscriptionInfo(customer);
              const trialLocked = subInfo?.status === 'active';
              return (
              <tr key={customer.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {customer.company_name || customer.companyName}
                    </div>
                    <div className="text-sm text-gray-500">
                      {customer.industry || 'Not specified'}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm text-gray-900">{customer.contact_name || customer.contactName || 'N/A'}</div>
                    <div className="text-sm text-gray-500">{customer.contact_email || customer.contactEmail}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                    {customer.subscription_plan || customer.subscriptionPlan || 'starter'}
                  </span>
                  {subInfo && (
                    <div className="mt-1">
                      <SubscriptionPill status={subInfo.status} trialEndsAt={subInfo.trialEndsAt} />
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getStatusBadge(customer)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <div>{customer.documents_count || customer.documentsCount || 0} docs</div>
                  <div className="text-xs text-gray-500">{(customer.queries_this_month || customer.queriesThisMonth || 0).toLocaleString()} queries</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatDate(customer.created_at || customer.createdAt)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex justify-end space-x-2">
                    <button
                      onClick={() => handleViewCustomer(customer)}
                      className="text-indigo-600 hover:text-indigo-900"
                      title="View Details"
                    >
                      <EyeIcon className="h-4 w-4" />
                    </button>
                    <button
                      className="text-gray-600 hover:text-gray-900"
                      title="Edit"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => !trialLocked && openTrialForCustomer(customer)}
                      disabled={trialLocked}
                      className={
                        trialLocked
                          ? 'text-cleona-600 opacity-50 cursor-not-allowed'
                          : 'text-cleona-600 hover:text-cleona-700'
                      }
                      title={trialLocked ? 'Paying customer — cannot modify trial' : 'Start / Extend Trial'}
                    >
                      <ClockIcon className="h-4 w-4" />
                    </button>
                    {(customer.is_active ?? customer.isActive) ? (
                      <button
                        onClick={() => handleSuspend(customer)}
                        className="text-red-600 hover:text-red-900"
                        title="Suspend"
                        disabled={suspendMutation.isLoading}
                      >
                        <NoSymbolIcon className="h-4 w-4" />
                      </button>
                    ) : (
                      <button
                        onClick={() => handleActivate(customer)}
                        className="text-green-600 hover:text-green-900"
                        title="Activate"
                        disabled={activateMutation.isLoading}
                      >
                        <CheckCircleIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Trial Modal */}
      {trialTarget && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-6 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {trialTarget === 'new' ? 'Start Trial for Unlisted User' : `Start / Extend Trial — ${trialTarget.company_name || trialTarget.companyName}`}
              </h3>
              <button onClick={closeTrialModal} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
            </div>

            {trialTarget === 'new' && (
              <p className="text-sm text-gray-600 mb-4">
                Use this for a Cognito user who signed up but never completed checkout. Paste their Cognito sub as Tenant ID (find it in AWS Cognito console → User details).
              </p>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tenant ID</label>
                <input
                  type="text"
                  value={trialForm.tenant_id}
                  onChange={(e) => setTrialForm({ ...trialForm, tenant_id: e.target.value })}
                  readOnly={trialTarget !== 'new'}
                  placeholder="e.g. 14c8d4c8-1021-7019-5a34-148419860888"
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm ${trialTarget !== 'new' ? 'bg-gray-50 text-gray-500' : ''}`}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Name{trialTarget === 'new' && <span className="text-red-500"> *</span>}</label>
                  <input
                    type="text"
                    value={trialForm.company_name}
                    onChange={(e) => setTrialForm({ ...trialForm, company_name: e.target.value })}
                    readOnly={trialTarget !== 'new'}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md ${trialTarget !== 'new' ? 'bg-gray-50 text-gray-500' : ''}`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email{trialTarget === 'new' && <span className="text-red-500"> *</span>}</label>
                  <input
                    type="email"
                    value={trialForm.email}
                    onChange={(e) => setTrialForm({ ...trialForm, email: e.target.value })}
                    readOnly={trialTarget !== 'new'}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md ${trialTarget !== 'new' ? 'bg-gray-50 text-gray-500' : ''}`}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
                  <select
                    value={trialForm.plan}
                    onChange={(e) => setTrialForm({ ...trialForm, plan: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="starter">Starter</option>
                    <option value="pro">Pro</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Trial Days</label>
                  <input
                    type="number"
                    min="1"
                    max="365"
                    value={trialForm.days}
                    onChange={(e) => setTrialForm({ ...trialForm, days: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-6 border-t mt-6">
              <button
                onClick={closeTrialModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitTrial}
                disabled={trialMutation.isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-cleona-600 rounded-md hover:bg-cleona-700 disabled:opacity-50"
              >
                {trialMutation.isLoading ? 'Saving...' : (trialTarget === 'new' ? 'Create Trial' : 'Set Trial')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Customer Detail Modal */}
      {selectedCustomer && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Customer Details</h3>
                <button
                  onClick={() => setSelectedCustomer(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Company Name</label>
                    <p className="text-sm text-gray-900">{selectedCustomer.company_name || selectedCustomer.companyName}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Industry</label>
                    <p className="text-sm text-gray-900">{selectedCustomer.industry || 'Not specified'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Contact Name</label>
                    <p className="text-sm text-gray-900">{selectedCustomer.contact_name || selectedCustomer.contactName || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Contact Email</label>
                    <p className="text-sm text-gray-900">{selectedCustomer.contact_email || selectedCustomer.contactEmail}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Subscription Plan</label>
                    <p className="text-sm text-gray-900 capitalize">{selectedCustomer.subscription_plan || selectedCustomer.subscriptionPlan || 'starter'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Tenant ID</label>
                    <p className="text-sm text-gray-900 font-mono">{selectedCustomer.tenant_id || selectedCustomer.tenantId}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Documents Uploaded</label>
                    <p className="text-sm text-gray-900">{selectedCustomer.documents_count || selectedCustomer.documentsCount || 0}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Queries This Month</label>
                    <p className="text-sm text-gray-900">{(selectedCustomer.queries_this_month || selectedCustomer.queriesThisMonth || 0).toLocaleString()}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Account Created</label>
                    <p className="text-sm text-gray-900">{formatDate(selectedCustomer.created_at || selectedCustomer.createdAt)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Last Activity</label>
                    <p className="text-sm text-gray-900">{formatDate(selectedCustomer.last_activity || selectedCustomer.lastActivity || selectedCustomer.created_at || selectedCustomer.createdAt)}</p>
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    onClick={() => setSelectedCustomer(null)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Close
                  </button>
                  <button className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700">
                    Edit Customer
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}